import { utcDate, getUtcOffset, guessTimezone } from '@/lib/utils/date';
import type { ScheduleGrid } from '@peach/types/schedule/schedule.dto.js';
import type { Weekday } from '@peach/types/schedule/types.js';
import { type DayOffRange, normalizeDayOffs } from '@peach/types/schedule/day-off.js';
import type { ScheduleUpdateBody } from '@peach/types/tutors/schedule-update.dto.js';
import type { TimeSlot } from '@peach/types/tutors/schedule-week.dto.js';

export interface AvailabilityBlock {
  id: string;
  day: number; // 0..6 (Mon..Sun)
  start: string; // HH:mm
  end: string; // HH:mm
}

export interface AvailabilityState {
  blocks: AvailabilityBlock[];
  disabledDays: number[]; // array of day ids 0..6
  timezone?: string; // IANA timezone, e.g. Europe/London
  dayOffs?: DayOffRange[]; // closed date ranges (YYYY-MM-DD); single day = from === to
  bufferMinutes?: number; // buffer time in minutes between sessions
  dailySessionCap?: number | null;
}

/** Local calendar date as YYYY-MM-DD (day offs are tutor-local dates). */
export function todayISODate(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Drop day-off ranges that ended before today — inert (booking looks forward only). */
export function dropPastDayOffs(ranges: DayOffRange[], today = todayISODate()): DayOffRange[] {
  return ranges.filter((r) => r.to >= today);
}

function key(tutorUsername: string) {
  return `meetmy_tutor_availability_${tutorUsername.toLowerCase()}`;
}

function getDefaultAvailability(tutorUsername: string): AvailabilityBlock[] {
  return [
    { id: `d_${tutorUsername}_0`, day: 0, start: '10:00', end: '13:00' },
    { id: `d_${tutorUsername}_1`, day: 2, start: '16:00', end: '18:00' },
    { id: `d_${tutorUsername}_2`, day: 4, start: '11:00', end: '14:00' },
  ];
}

export function getDefaultAvailabilityState(tutorUsername: string): AvailabilityState {
  return {
    blocks: getDefaultAvailability(tutorUsername),
    disabledDays: [5, 6],
    timezone: 'Europe/London',
    dayOffs: [],
  };
}

export function saveAvailabilityState(tutorUsername: string, state: AvailabilityState) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key(tutorUsername), JSON.stringify(state));
}

const DAY_NAMES: Weekday[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

function dayNumberToName(day: number): Weekday {
  return DAY_NAMES[day];
}

function timeToDate(time: string, utcOffset: number): Date {
  const [h, m] = time.split(':').map(Number);
  return utcDate('1970-01-01')
    .startOf('day')
    .add(h, 'hour')
    .add(m, 'minute')
    .subtract(utcOffset, 'minute')
    .toDate();
}

function dateToTime(date: Date, utcOffset: number): string {
  const d = utcDate(date).add(utcOffset, 'minute');
  if (d.hour() === 23 && d.minute() === 59) return '24:00';
  return d.format('HH:mm');
}

// Cross-midnight slot detection and splitting
// Uses the same date markers as backend: 1970-01-01 (current), 1970-01-02 (next), 1969-12-31 (prev)
interface ProcessedSlot {
  currentDay?: { start: Date; end: Date };
  nextDay?: { start: Date; end: Date };
  prevDay?: { start: Date; end: Date };
}

// Convert a time to the display timezone and normalize to base date (1970-01-01)
function toLocalBaseDate(dateInput: Date | string, utcOffset: number): Date {
  const d = utcDate(dateInput).add(utcOffset, 'minute');
  return utcDate('1970-01-01')
    .hour(d.hour())
    .minute(d.minute())
    .second(d.second())
    .millisecond(d.millisecond())
    .toDate();
}

function processSlotForDisplay(slot: TimeSlot, utcOffset: number): ProcessedSlot {
  // Convert to display timezone to determine day assignments
  const startLocal = utcDate(slot.start).add(utcOffset, 'minute');
  const endLocal = utcDate(slot.end).add(utcOffset, 'minute');

  const startDate = startLocal.format('YYYY-MM-DD');
  const endDate = endLocal.format('YYYY-MM-DD');

  const slotEndUtc = utcDate(slot.end);
  const isEndAtUtcMidnight =
    slotEndUtc.format('YYYY-MM-DD') === '1970-01-01' && slotEndUtc.hour() === 23;

  // Same day in local time - no splitting needed
  if (startDate === '1970-01-01' && endDate === '1970-01-01') {
    return {
      currentDay: {
        start: toLocalBaseDate(slot.start, utcOffset),
        end: toLocalBaseDate(slot.end, utcOffset),
      },
    };
  }

  // Entire slot is on next day (1970-01-02) in local time - move entirely to next weekday
  if (startDate === '1970-01-02' && endDate === '1970-01-02') {
    return {
      nextDay: {
        start: toLocalBaseDate(slot.start, utcOffset),
        end: toLocalBaseDate(slot.end, utcOffset),
      },
    };
  }

  // Entire slot is on prev day (1969-12-31) in local time - move entirely to prev weekday
  if (startDate === '1969-12-31' && endDate === '1969-12-31') {
    return {
      prevDay: {
        start: toLocalBaseDate(slot.start, utcOffset),
        end: toLocalBaseDate(slot.end, utcOffset),
      },
    };
  }

  // Crosses from current day (1970-01-01) to next day (1970-01-02) in local time - split
  if (startDate === '1970-01-01' && endDate === '1970-01-02') {
    const isEndAtLocalMidnight =
      endLocal.hour() === 0 &&
      endLocal.minute() === 0 &&
      endLocal.second() === 0 &&
      endLocal.millisecond() === 0;

    if (isEndAtUtcMidnight || isEndAtLocalMidnight) {
      return {
        currentDay: {
          start: toLocalBaseDate(slot.start, utcOffset),
          end: new Date('1970-01-01T23:59:59.999Z'),
        },
      };
    }
    return {
      currentDay: {
        start: toLocalBaseDate(slot.start, utcOffset),
        end: new Date('1970-01-01T23:59:59.999Z'),
      },
      nextDay: {
        start: new Date('1970-01-01T00:00:00.000Z'),
        end: toLocalBaseDate(slot.end, utcOffset),
      },
    };
  }

  // Crosses from prev day (1969-12-31) to current day (1970-01-01) in local time - split
  if (startDate === '1969-12-31' && endDate === '1970-01-01') {
    return {
      prevDay: {
        start: toLocalBaseDate(slot.start, utcOffset),
        end: new Date('1970-01-01T23:59:59.999Z'),
      },
      currentDay: {
        start: new Date('1970-01-01T00:00:00.000Z'),
        end: toLocalBaseDate(slot.end, utcOffset),
      },
    };
  }

  // Default: return as-is with local time conversion
  return {
    currentDay: {
      start: toLocalBaseDate(slot.start, utcOffset),
      end: toLocalBaseDate(slot.end, utcOffset),
    },
  };
}

function getNextWeekday(day: number): number {
  return (day + 1) % 7;
}

function getPrevWeekday(day: number): number {
  return (day + 6) % 7;
}

export interface ScheduleDto {
  grid?: ScheduleGrid;
  dayOffs?: DayOffRange[];
  timezone?: string;
  bufferMinutes?: number;
  dailySessionCap?: number | null;
}

export function toScheduleUpdateBody(
  state: AvailabilityState,
  timezone: string
): ScheduleUpdateBody {
  const utcOffset = getUtcOffset(timezone, new Date());

  const slotsByDay: Map<number, TimeSlot[]> = new Map();
  for (let i = 0; i < 7; i++) {
    slotsByDay.set(i, []);
  }

  for (let day = 0; day <= 6; day++) {
    if (state.disabledDays.includes(day)) continue;
    const dayBlocks = state.blocks.filter((b) => b.day === day);

    for (const block of dayBlocks) {
      const startUtc = timeToDate(block.start, utcOffset);
      const endUtc = timeToDate(block.end, utcOffset);

      slotsByDay.get(day)!.push({ start: startUtc, end: endUtc });
    }
  }

  const grid: ScheduleGrid = {};
  for (let day = 0; day <= 6; day++) {
    const slots = slotsByDay.get(day)!;
    if (slots.length > 0) {
      const dayName = dayNumberToName(day);
      (grid as Record<string, TimeSlot[]>)[dayName] = slots;
    }
  }

  return {
    grid,
    dayOffs: state.dayOffs ?? [],
    timezone,
    dailySessionCap: state.dailySessionCap ?? null,
  };
}

export function fromScheduleDto(
  schedule: ScheduleDto | undefined,
  tutorUsername: string
): AvailabilityState {
  if (!schedule || !schedule.grid) {
    return getDefaultAvailabilityState(tutorUsername);
  }

  const timezone = guessTimezone();
  const utcOffset = getUtcOffset(timezone, new Date());

  // Collect blocks per day (some days may get additional blocks from cross-midnight splits)
  const blocksByDay: Map<number, Array<{ start: Date; end: Date }>> = new Map();
  for (let i = 0; i < 7; i++) {
    blocksByDay.set(i, []);
  }

  // Process each day's slots, handling cross-midnight splits
  for (let day = 0; day <= 6; day++) {
    const dayName = dayNumberToName(day);
    const slots = (schedule.grid as Record<string, TimeSlot[] | undefined>)[dayName];

    if (!slots || slots.length === 0) {
      continue;
    }

    for (const slot of slots) {
      const slotStart = utcDate(slot.start).valueOf();
      const slotEnd = utcDate(slot.end).valueOf();
      if (slotStart >= slotEnd) continue;

      const processed = processSlotForDisplay(slot, utcOffset);

      // Add current day portion
      if (processed.currentDay) {
        blocksByDay.get(day)!.push(processed.currentDay);
      }

      // Add next day portion (if slot crosses midnight in local time)
      if (processed.nextDay) {
        const nextDay = getNextWeekday(day);
        blocksByDay.get(nextDay)!.push(processed.nextDay);
      }

      // Add previous day portion (less common case)
      if (processed.prevDay) {
        const prevDay = getPrevWeekday(day);
        blocksByDay.get(prevDay)!.push(processed.prevDay);
      }
    }
  }

  // Convert collected blocks to AvailabilityBlock format
  const blocks: AvailabilityBlock[] = [];
  const disabledDays: number[] = [];

  for (let day = 0; day <= 6; day++) {
    const dayBlocks = blocksByDay.get(day)!;

    if (dayBlocks.length === 0) {
      disabledDays.push(day);
      continue;
    }

    // Sort blocks by start time
    dayBlocks.sort((a, b) => a.start.getTime() - b.start.getTime());

    for (const block of dayBlocks) {
      const id = `ab_${tutorUsername}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
      blocks.push({
        id,
        day,
        start: dateToTime(block.start, 0),
        end: dateToTime(block.end, 0),
      });
    }
  }

  return {
    blocks,
    disabledDays,
    timezone,
    dayOffs: normalizeDayOffs(schedule.dayOffs),
    bufferMinutes: schedule.bufferMinutes ?? 0,
    dailySessionCap: schedule.dailySessionCap ?? null,
  };
}
